import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { v4 as uuidv4 } from 'uuid';
import { LoginTrackingApiService } from './login-tracking-api.service';
import { StorageService } from './storage.service';
import { AppConfig } from '../../app.config';

export const LOGIN_STAGE = {
  LOGIN_INIT: 'LOGIN_INIT',
  LOGIN_SKIPPED: 'LOGIN_SKIPPED',
  LOGIN_ABORTED: 'LOGIN_ABORTED',
  SIGNOUT_SKIPPED: 'SIGNOUT_SKIPPED',
  SIGNALR_INIT: 'SIGNALR_INIT',
  SIGNALR_TIMER_STARTED: 'SIGNALR_TIMER_STARTED',
  SIGNALR_TIMER_SKIPPED: 'SIGNALR_TIMER_SKIPPED',
  SIGNALR_TIMER_CHECK_OK: 'SIGNALR_TIMER_CHECK_OK',
  SIGNALR_CONNECTION_STARTED: 'SIGNALR_CONNECTION_STARTED',
  SIGNALR_CONNECTION_FAILED: 'SIGNALR_CONNECTION_FAILED',
  SIGNALR_TOKEN_RECEIVED: 'SIGNALR_TOKEN_RECEIVED',
  SIGNALR_TOKEN_EXPIRED: 'SIGNALR_TOKEN_EXPIRED',
  SIGNALR_TOKEN_EXPIRY_CHECK: 'SIGNALR_TOKEN_EXPIRY_CHECK',
  SIGNALR_TOKEN_EXPIRY_CONFIRMED: 'SIGNALR_TOKEN_EXPIRY_CONFIRMED',
  SIGNALR_NOT_CONNECTED: 'SIGNALR_NOT_CONNECTED',
  SIGNALR_CONNECTION_RESTARTED: 'SIGNALR_CONNECTION_RESTARTED',
  SIGNALR_CONNECTION_RESTART_FAILED: 'SIGNALR_CONNECTION_RESTART_FAILED',
  SIGNALR_CONNECTION_START_SKIPPED: 'SIGNALR_CONNECTION_START_SKIPPED',
  SIGNALR_CONNECTION_START_ATTEMPT: 'SIGNALR_CONNECTION_START_ATTEMPT',
  BROWSER_OPENED: 'BROWSER_OPENED',
  BROWSER_CLOSED: 'BROWSER_CLOSED',
  BROWSER_CLOSE_FAILED: 'BROWSER_CLOSE_FAILED',
  AUTH_CODE_RECEIVED_INVOKED: 'AUTH_CODE_RECEIVED_INVOKED',
  AUTH_CODE_RECEIVED_SUCCESS: 'AUTH_CODE_RECEIVED_SUCCESS',  
  AUTH_CODE_RECEIVED_FAILED: 'AUTH_CODE_RECEIVED_FAILED',
  WEB_TOKEN_SUCCESS: 'WEB_TOKEN_SUCCESS',
  WEB_TOKEN_FAILED: 'WEB_TOKEN_FAILED',
  TOKEN_EXCHANGE_INIT: 'TOKEN_EXCHANGE_INIT',
  TOKEN_EXCHANGE_DUPLICATE: 'TOKEN_EXCHANGE_DUPLICATE',
  TOKEN_EXCHANGE_RESPONSE: 'TOKEN_EXCHANGE_RESPONSE',
  TOKEN_EXCHANGE_SUCCESS: 'TOKEN_EXCHANGE_SUCCESS',
  TOKEN_EXCHANGE_FAILED: 'TOKEN_EXCHANGE_FAILED',
  PROFILE_FETCH_FAILED: 'PROFILE_FETCH_FAILED',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILED: 'LOGIN_FAILED',
  TOKEN_VALIDATION_SUCCESS: 'TOKEN_VALIDATION_SUCCESS',
  TOKEN_VALIDATION_FAILED: 'TOKEN_VALIDATION_FAILED',
  AUTH_MIDDLEWARE_INVOKED: 'AUTH_MIDDLEWARE_INVOKED',
  AUTH_MIDDLEWARE_FAILED: 'AUTH_MIDDLEWARE_FAILED',
  AUTH_MIDDLEWARE_SUCCESS: 'AUTH_MIDDLEWARE_SUCCESS',
  AUTH_MIDDLEWARE_DUPLICATE_CALLBACK: 'AUTH_MIDDLEWARE_DUPLICATE_CALLBACK',
  REDIRECT_TO_B2C_FAILED: 'REDIRECT_TO_B2C_FAILED',
  REDIRECT_TO_B2C_SUCCESS: 'REDIRECT_TO_B2C_SUCCESS'
} as const;

@Injectable({
  providedIn: 'root',
})
export class LoginTrackingService {
  public sessionId: string = '';
  public deviceId: string = '';
  private readonly platform: string = Capacitor.getPlatform();
  private appVersion: string = '';  
  private osVersion: string = '';
  private model: string = '';
  private manufacturer: string = '';
  private readonly deviceInfoReady: Promise<void>;

  constructor(
    private readonly loginTrackingApiService: LoginTrackingApiService,
    private readonly storageService: StorageService
  ) {
    this.deviceInfoReady = this.initDeviceInfo();
  }

  private async initDeviceInfo() {
    if (Capacitor.isNativePlatform()) {
      try {
        const [appInfo, deviceInfo] = await Promise.all([
          App.getInfo(),
          Device.getInfo(),
        ]);
        this.appVersion = appInfo.version;
        this.osVersion = deviceInfo.osVersion || '';
        this.model = deviceInfo.model || '';
        this.manufacturer = deviceInfo.manufacturer || '';
      } catch {
        // ignore — not critical
      }
    }

    // Persist a stable device ID across sessions
    const stored = this.storageService.getDataFromStorage('css_device_id');
    if (stored) {
      this.deviceId = stored;
    } else {
      this.deviceId = uuidv4();
      this.storageService.putDataInStorage('css_device_id', this.deviceId);
    }
  }

  startNewSession(): string {
    this.sessionId = uuidv4();
    return this.sessionId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  track(stage: string, message?: string, sessionId?: string, deviceId?: string, platform?: string) {

    if(this.platform === 'android' && 
      !AppConfig.settings?.ANDROID_APP_LOGIN_TRACKING_ENABLED && 
      !AppConfig.settings?.LOGIN_TRACKING_ENABLED){
      return;
    }

    if(
      this.platform === 'ios' && 
      !AppConfig.settings?.IOS_APP_LOGIN_TRACKING_ENABLED && 
      !AppConfig.settings?.LOGIN_TRACKING_ENABLED
    ){
      return;
    }

    if(
      this.platform === 'web' && 
      !AppConfig.settings?.WEB_APP_LOGIN_TRACKING_ENABLED && 
      !AppConfig.settings?.LOGIN_TRACKING_ENABLED
    ){
      return;
    }

    this.deviceInfoReady.then(() => {
      if (!sessionId && !this.sessionId) {
        this.startNewSession();
      }

      const payload = {
        session_id: sessionId || this.sessionId,
        stage,
        platform: platform || this.platform,
        app_version: this.appVersion || null,
        device_id: deviceId || this.deviceId || null,
        os_version: this.osVersion || null,
        model: this.model || null,
        manufacturer: this.manufacturer || null,
        message: message || null,
      };

      this.loginTrackingApiService.trackLoginStep(payload).subscribe({
        error: (err) =>
          console.warn(`LoginTracking: failed to track stage [${stage}]`, err),
      });
    });    
  }
}